import {faPlus} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import type {Moment} from 'moment-timezone'
import {maybe} from '@freckle/maybe'
import DateOptions from '@freckle/educator-entities/ts/common/helpers/date-options'
import type {DateOption} from '@freckle/educator-entities/ts/common/helpers/date-options'
import {humanReadableTime} from '@freckle/educator-entities/ts/common/helpers/time-helpers'
import type {TranslateT} from '@freckle/educator-entities/ts/common/helpers/translate'
import type {GenericAssignmentSessionAttrsT} from '@freckle/educator-entities/ts/common/models/generic-assignment-session'
import {MathAssignmentTypes} from '@freckle/educator-entities/ts/math/common/models/math-assignment'
import type {MathFactPracticeOperationT} from '@freckle/educator-entities/ts/math/fact-practice/models/math-fact-practice-operation'
import {formattedName} from '@freckle/educator-entities/ts/users/models/student'
import type {StudentAttrs} from '@freckle/educator-entities/ts/users/models/student'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {ExpandingTable} from '@freckle/classroom/ts/common/components/expanding-table'
import type {HeaderCell, RowCell} from '@freckle/classroom/ts/common/components/expanding-table'
import {MenuBar} from '@freckle/classroom/ts/common/components/menu-bar'
import {NoDataNotice} from '@freckle/classroom/ts/common/components/no-data-notice'
import {SessionHistoryMenu} from '@freckle/classroom/ts/common/components/session-history-menu'
import {
  generateFactPracticeRows,
  factPracticeRowSortValue,
} from '@freckle/classroom/ts/math/fact-practice/logic/fact-practice-session-history-logic'
import type {
  FactPracticeRow,
  FactPracticeDetail,
  FactPracticeRowKey,
} from '@freckle/classroom/ts/math/fact-practice/logic/fact-practice-session-history-logic'
import {FactPracticeLevelTooltip} from './fact-practice-level-tooltip'
import {
  noticeBarReportDescription,
  reportLabel,
  reportDescription,
} from '../../../../common/components/no-data-notice.module.scss'

type Props = {
  genericSessions: Array<GenericAssignmentSessionAttrsT>
  students: Array<StudentAttrs>
  dateOption: DateOption
  from: Moment
  to: Moment
  isPremium: boolean
  navigateFactPracticeAssign: () => void
  initiallyExpandedRowStudentId: number | null | undefined
  allMathFactPracticeOperations: Array<MathFactPracticeOperationT>
}

export function FactPracticeSessionHistory({
  genericSessions,
  students,
  dateOption,
  from,
  to,
  isPremium,
  navigateFactPracticeAssign,
  initiallyExpandedRowStudentId,
  allMathFactPracticeOperations,
}: Props) {
  const {t} = useSafeTranslation()
  const renderDurationIfPremium = (seconds: number): string =>
    textIfPremium(isPremium, () => humanReadableTime(seconds))

  function renderHeader(t: TranslateT): Array<HeaderCell<FactPracticeRowKey>> {
    const [coinsLabel, coinsKey] =
      dateOption === DateOptions.Today
        ? [t('FACT_PRACTICE_SESSION_HISTORY_TABLE_HEADER_COINS_TODAY'), 'coinsToday']
        : [t('FACT_PRACTICE_SESSION_HISTORY_TABLE_HEADER_COINS_TOTAL'), 'coinsTotal']
    return [
      {label: t('FACT_PRACTICE_SESSION_HISTORY_TABLE_HEADER_NAME'), sortBy: 'student'},
      {label: coinsLabel, sortBy: coinsKey as FactPracticeRowKey},
      {label: t('FACT_PRACTICE_SESSION_HISTORY_TABLE_HEADER_QUESTIONS'), sortBy: 'questions'},
      {label: t('FACT_PRACTICE_SESSION_HISTORY_TABLE_HEADER_ACCURACY'), sortBy: 'accuracy'},
      {label: t('FACT_PRACTICE_SESSION_HISTORY_TABLE_HEADER_TIME_SPENT'), sortBy: 'timeSpent'},
    ]
  }

  function renderRow(row: FactPracticeRow): Array<RowCell> {
    const coins = dateOption === DateOptions.Today ? row.coinsToday : row.coinsTotal
    return [
      {value: formattedName(row.student)},
      {value: coins.toString()},
      {value: row.questions.toString()},
      {value: `${row.accuracy.toFixed(1)}%`},
      {value: renderDurationIfPremium(row.timeSpent)},
    ]
  }

  const keyPrefix = MathAssignmentTypes.forDisplay(MathAssignmentTypes.MathFactPractice)

  const assignFactPractice = (
    <span>
      <FontAwesomeIcon icon={faPlus} /> {t('FACT_PRACTICE_SESSION_HISTORY_ASSIGN_FACT_PRACTICE')}
    </span>
  )

  const table = (
    <ExpandingTable
      key={`${keyPrefix}-${dateOption}`}
      getData={() =>
        generateFactPracticeRows(allMathFactPracticeOperations, genericSessions, students)
      }
      filterBy={row => formattedName(row.student)}
      sortBy={'student'}
      sortValue={(key: string, row: FactPracticeRow) =>
        factPracticeRowSortValue(key as FactPracticeRowKey, row)
      }
      renderHeader={() => renderHeader(t)}
      renderRow={row => renderRow(row)}
      renderDetailHeader={() => renderDetailHeader(t)}
      renderDetailRow={(...rest) => renderDetailRow(allMathFactPracticeOperations, t, ...rest)}
      studentIdOfRowToBeExpanded={initiallyExpandedRowStudentId}
    >
      <NoDataNotice
        subject="math"
        imageName="math/fact-practice/empty-session-report"
        onButtonClick={navigateFactPracticeAssign}
        noticeBarLeftElement={
          <div className={noticeBarReportDescription}>
            <span className={reportLabel}>
              {t('FACT_PRACTICE_SESSION_HISTORY_TABLE_NO_DATA_NOTICE_LABEL')}
            </span>
            <span className={reportDescription}>
              {t('FACT_PRACTICE_SESSION_HISTORY_NO_DATA_NOTICE_DESCRIPTION')}
            </span>
          </div>
        }
        noticeBarRightButtonBody={assignFactPractice}
        textToOverlay={t('FACT_PRACTICE_SESSION_HISTORY_TABLE_NO_DATA_OVERLAY_MESSAGE')}
      />
    </ExpandingTable>
  )

  return (
    <div className="row no-margin-row">
      <div className="col-12">
        <MenuBar>
          <SessionHistoryMenu
            key={`${keyPrefix}-session-history-menu`}
            sessionType={MathAssignmentTypes.MathFactPractice}
            isPremium={isPremium}
            dateOption={dateOption}
            from={from}
            to={to}
          />
        </MenuBar>

        <div className="row std-margin-top-md">
          <div className="col-12">{table}</div>
        </div>
      </div>
    </div>
  )
}

function renderLevelHeader(t: TranslateT) {
  return (
    <FactPracticeLevelTooltip>
      <>{t('FACT_PRACTICE_SESSION_HISTORY_DETAIL_TABLE_HEADER_LEVEL')}</>
    </FactPracticeLevelTooltip>
  )
}

function renderDetailHeader(t: TranslateT): Array<HeaderCell<void>> {
  return [
    {label: t('FACT_PRACTICE_SESSION_HISTORY_DETAIL_TABLE_HEADER_SESSION_END_TIME')},
    {label: t('FACT_PRACTICE_SESSION_HISTORY_DETAIL_TABLE_HEADER_OPERATION')},
    {label: renderLevelHeader(t)},
    {label: t('FACT_PRACTICE_SESSION_HISTORY_DETAIL_TABLE_HEADER_QUESTIONS')},
    {label: t('FACT_PRACTICE_SESSION_HISTORY_DETAIL_TABLE_HEADER_ACCURACY')},
  ]
}

function renderDetailRow(
  allMathFactPracticeOperations: Array<MathFactPracticeOperationT>,
  t: TranslateT,
  detail: FactPracticeDetail,
): Array<RowCell> {
  return [
    {
      value: detail.timeEnd.format(
        t('FACT_PRACTICE_SESSION_HISTORY_TABLE_SESSION_END_DATE_FORMAT'),
      ),
    },
    {
      value: maybe(
        () => detail.operation,
        operation => operation.description,
        allMathFactPracticeOperations.find(operation => operation.operation === detail.operation),
      ),
    },
    {value: detail.level.toString()},
    {
      value: maybe(
        () => t('FACT_PRACTICE_SESSION_HISTORY_TABLE_ROW_NA'),
        qs => qs.toString(),
        detail.questions,
      ),
    },
    {
      value: `${maybe(
        () => t('FACT_PRACTICE_SESSION_HISTORY_TABLE_ROW_NA'),
        v => v.toString(),
        detail.accuracy,
      )}%`,
    },
  ]
}

function textIfPremium(isPremium: boolean, thunk: () => string): string {
  return isPremium ? thunk() : 'Available in Premium Edition'
}
